import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { collection, doc, getDoc, getDocs, serverTimestamp, updateDoc } from "firebase/firestore";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { db } from "../../firebase/config";
import PageShell from "../layout/PageShell";
import { Store } from "../type/Store";
import {
  PRODUCT_VERSIONS_COLLECTION,
  ProductVersion,
  productVersionLabel,
} from "../../productVersions/types";
import {
  getPlanDisplayName,
  getPlanPrice,
} from "../../utils/subscription/planFeatures";
import { startStoreProduction } from "../../stores/startProduction";
import RegisterStationPanel from "./RegisterStationPanel";
import { BillingSubactivity, billingSubactivityOf } from "./createStoreRecord";

const ACTIVITY_LABEL: Record<BillingSubactivity, string> = {
  RES: "Restaurant",
  BAR: "Bar",
  CDR: "Food truck",
};

const StorePage: React.FC = () => {
  const { storeId } = useParams();
  const [searchParams] = useSearchParams();
  const tabQuery = searchParams.get("tab");
  const [tabIndex, setTabIndex] = useState(tabQuery === "station" ? 1 : 0);
  useEffect(() => {
    setTabIndex(tabQuery === "station" ? 1 : 0);
  }, [tabQuery]);
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState<ProductVersion[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [emailReceiptAllowed, setEmailReceiptAllowed] = useState(false);
  const [receiptPoweredByNuage, setReceiptPoweredByNuage] = useState(true);
  const [customerScreenAlwaysOn, setCustomerScreenAlwaysOn] = useState(false);
  const [productVersionId, setProductVersionId] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("essential");
  const [subscriptionStatus, setSubscriptionStatus] = useState("active");
  const [storedActivity, setStoredActivity] = useState<BillingSubactivity>("RES");
  const [billingSubactivity, setBillingSubactivity] = useState<BillingSubactivity>("RES");
  const [activityWord, setActivityWord] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [admins, setAdmins] = useState<string[]>([]);
  const [storeMode, setStoreMode] = useState<"sandbox" | "production">("sandbox");
  const [productionCutoff, setProductionCutoff] = useState("");
  const [cutoffDate, setCutoffDate] = useState("");
  const [confirmWord, setConfirmWord] = useState("");
  const [switching, setSwitching] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [storeSnap, versionSnap] = await Promise.all([
          getDoc(doc(db, "stores", storeId)),
          getDocs(collection(db, PRODUCT_VERSIONS_COLLECTION)),
        ]);
        if (cancelled) return;
        if (!storeSnap.exists()) {
          toast({ title: "Store not found", status: "error" });
          navigate("/restaurants");
          return;
        }
        const store = { id: storeSnap.id, ...storeSnap.data() } as Store;
        const loadedVersions = versionSnap.docs.map(
          (item) => ({ id: item.id, ...(item.data() as Omit<ProductVersion, "id">) })
        );
        loadedVersions.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
        const fallback = loadedVersions.find((item) => item.isDefault && item.isActive !== false)?.id
          || loadedVersions.find((item) => item.isActive !== false)?.id
          || loadedVersions[0]?.id
          || "";
        setVersions(loadedVersions);
        setName(store.name || "");
        setEmail(store.email || "");
        setPhone(store.phone || "");
        setAddress(store.address || "");
        setEmailReceiptAllowed(store.emailReceiptAllowed === true);
        setReceiptPoweredByNuage(store.receiptPoweredByNuage !== false);
        setCustomerScreenAlwaysOn(store.customerScreenAlwaysOn === true);
        setProductVersionId(store.productVersionId || fallback);
        setSubscriptionPlan(store.subscriptionPlan || "essential");
        setSubscriptionStatus(store.subscriptionStatus || "active");
        const activity = billingSubactivityOf(store.billingSubactivity);
        setStoredActivity(activity);
        setBillingSubactivity(activity);
        setActivityWord("");
        setCreatedBy(store.createdBy || "");
        setAdmins(store.admins || []);
        setStoreMode(store.storeMode === "production" ? "production" : "sandbox");
        setProductionCutoff(store.productionCutoff || "");
      } catch (error: any) {
        if (!cancelled) {
          toast({ title: "Could not load store", description: error.message, status: "error" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [storeId, navigate, toast]);

  useEffect(() => {
    if (tabQuery === "station") setTabIndex(1);
  }, [tabQuery]);

  const save = async () => {
    if (!storeId || !name.trim()) {
      toast({ title: "Store name is required", status: "error" });
      return;
    }
    const activityChanged = billingSubactivity !== storedActivity;
    if (activityChanged && activityWord.trim() !== "change") {
      toast({ title: "Type change to confirm the store activity", status: "error" });
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, "stores", storeId), {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: address.trim(),
        emailReceiptAllowed,
        receiptPoweredByNuage,
        customerScreenAlwaysOn,
        productVersionId: productVersionId || "",
        subscriptionStatus,
        ...(activityChanged ? { billingSubactivity } : {}),
        updated: serverTimestamp(),
      });
      if (activityChanged) {
        setStoredActivity(billingSubactivity);
        setActivityWord("");
      }
      toast({ title: "Store updated", status: "success" });
    } catch (error: any) {
      toast({ title: "Could not save store", description: error.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const price = getPlanPrice(subscriptionPlan);
  const priceLabel = price > 0 ? `$${price}/mo` : "$0";
  const selectableVersions = versions.filter(
    (version) => version.isActive !== false || version.id === productVersionId
  );

  const openProductionSwitch = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    setCutoffDate(`${today.getFullYear()}-${month}-${day}`);
    setConfirmWord("");
    setSwitchOpen(true);
  };

  const confirmProduction = async () => {
    if (!storeId || confirmWord.trim() !== "production" || !cutoffDate) return;
    setSwitching(true);
    try {
      await startStoreProduction(storeId, cutoffDate);
      setStoreMode("production");
      setProductionCutoff(cutoffDate);
      setSwitchOpen(false);
      toast({
        title: "Store is in production",
        description: "Testing orders, sessions, transactions, and reservations were removed. The menu and settings remain.",
        status: "success",
        duration: 6000,
      });
    } catch (error: any) {
      toast({ title: "Could not start production", description: error.message, status: "error" });
    } finally {
      setSwitching(false);
    }
  };

  return (
    <PageShell
      fluid
      title={name || "Store"}
      description="Store summary. Log in to the store to change its own settings."
      actions={
        <Button as={RouterLink} to="/restaurants" variant="outline">
          Back to stores
        </Button>
      }
    >
      {loading ? (
        <Spinner />
      ) : (
        <Tabs colorScheme="blue" variant="enclosed" index={tabIndex} onChange={setTabIndex}>
          <TabList>
            <Tab>Store</Tab>
            <Tab>Register Station</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={6}>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Store name</FormLabel>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input value={phone} onChange={(event) => setPhone(event.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Address</FormLabel>
              <Input value={address} onChange={(event) => setAddress(event.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Current plan</FormLabel>
              <Input value={getPlanDisplayName(subscriptionPlan)} isReadOnly bg="gray.50" />
              <Text fontSize="sm" color="gray.600" mt={2}>
                Change the plan from Subscriptions.
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel>Store activity</FormLabel>
              <Select
                value={billingSubactivity}
                onChange={(event) => {
                  setBillingSubactivity(billingSubactivityOf(event.target.value));
                  setActivityWord("");
                }}
              >
                {(Object.keys(ACTIVITY_LABEL) as BillingSubactivity[]).map((code) => (
                  <option key={code} value={code}>{ACTIVITY_LABEL[code]}</option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.600" mt={2}>
                Restaurant is the default. Bills use this activity for each priced item.
              </Text>
              {billingSubactivity !== storedActivity && (
                <Box mt={3}>
                  <FormLabel>Type change to confirm</FormLabel>
                  <Input value={activityWord} onChange={(event) => setActivityWord(event.target.value)} autoComplete="off" />
                </Box>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select value={subscriptionStatus} onChange={(event) => setSubscriptionStatus(event.target.value)}>
                <option value="active">Active</option>
                <option value="trialing">Trialing</option>
                <option value="past_due">Past due</option>
                <option value="canceled">Canceled</option>
                <option value="deactivated">Deactivated</option>
              </Select>
              <Text fontSize="sm" color="gray.600" mt={2}>
                A deactivated store cannot sign in or operate.
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel>Price</FormLabel>
              <Input value={priceLabel} isReadOnly bg="gray.50" />
            </FormControl>
            <FormControl>
              <FormLabel>Software version</FormLabel>
              <Select
                value={productVersionId}
                onChange={(event) => setProductVersionId(event.target.value)}
                placeholder={versions.length ? undefined : "No versions yet"}
              >
                {selectableVersions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {productVersionLabel(version)}
                    {version.isDefault ? " (default)" : ""}
                    {version.isActive === false ? " (inactive)" : ""}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.600" mt={2}>
                Default for a new workstation. A workstation that reports an installed version uses the profile for that version.
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel>Owner</FormLabel>
              <Input value={createdBy || "No owner"} isReadOnly bg="gray.50" />
            </FormControl>
            <FormControl>
              <FormLabel>Admins</FormLabel>
              {admins.length > 0 ? (
                <HStack spacing={2} wrap="wrap">
                  {admins.map((adminEmail) => (
                    <Badge key={adminEmail} colorScheme="gray">{adminEmail}</Badge>
                  ))}
                </HStack>
              ) : (
                <Text fontSize="sm" color="gray.600">No admins</Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Store mode</FormLabel>
              {storeMode === "production" ? (
                <Box>
                  <Badge colorScheme="green">Production</Badge>
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    Cutoff {productionCutoff || "recorded"}. Testing activity was cleared on that switch.
                  </Text>
                </Box>
              ) : (
                <Box>
                  <HStack>
                    <Badge colorScheme="orange">Sandbox</Badge>
                    <Button size="sm" variant="outline" onClick={openProductionSwitch}>
                      Switch to production
                    </Button>
                  </HStack>
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    Sandbox is for setup and testing. Production starts on a cutoff day and clears that testing activity.
                  </Text>
                </Box>
              )}
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box mr={4}>
                <FormLabel mb={0}>Customer screen always on</FormLabel>
                <Text fontSize="sm" color="gray.600">
                  The customer screen stays open for this store.
                </Text>
              </Box>
              <Switch
                isChecked={customerScreenAlwaysOn}
                onChange={(event) => setCustomerScreenAlwaysOn(event.target.checked)}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box mr={4}>
                <FormLabel mb={0}>Email receipts</FormLabel>
                <Text fontSize="sm" color="gray.600">
                  Allows the store owner to issue electronic receipts by email.
                </Text>
              </Box>
              <Switch
                isChecked={emailReceiptAllowed}
                onChange={(event) => setEmailReceiptAllowed(event.target.checked)}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box mr={4}>
                <FormLabel mb={0}>Powered by NuagePOS</FormLabel>
                <Text fontSize="sm" color="gray.600">
                  Prints Propulsé par NuagePOS.com under the TVQ number on each receipt.
                </Text>
              </Box>
              <Switch
                isChecked={receiptPoweredByNuage}
                onChange={(event) => setReceiptPoweredByNuage(event.target.checked)}
              />
            </FormControl>
            <HStack>
              <Button onClick={save} isLoading={saving}>
                Save
              </Button>
            </HStack>
          </VStack>
        </Box>
            </TabPanel>
            <TabPanel px={0}>
              {storeId && <RegisterStationPanel storeId={storeId} />}
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
      <Modal isOpen={switchOpen} onClose={() => setSwitchOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Switch to production</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text>
                Choose the cutoff day. Orders, sessions, transactions, and reservations from testing are removed. The menu, staff, and store settings stay.
              </Text>
              <FormControl>
                <FormLabel>Cutoff day</FormLabel>
                <Input type="date" value={cutoffDate} onChange={(event) => setCutoffDate(event.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Type production to confirm</FormLabel>
                <Input value={confirmWord} onChange={(event) => setConfirmWord(event.target.value)} autoComplete="off" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={() => setSwitchOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmProduction}
              isLoading={switching}
              isDisabled={confirmWord.trim() !== "production" || !cutoffDate}
            >
              Start production
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
};

export default StorePage;
